import { type ReactNode, useState } from "react";

export type SettingsSurfaceId =
  | "hall-structure"
  | "tanks"
  | "water-circuits"
  | "feeding-program"
  | "workers-permissions"
  | "hall-plan"
  | "notifications-devices"
  | "qr-printing"
  | "ai"
  | "system-version"
  | "treatment-protocols";

interface SettingsHubEntry {
  id: SettingsSurfaceId;
  label: string;
  description: string;
  notice?: string;
}

export const SETTINGS_HUB_ENTRIES: readonly SettingsHubEntry[] = [
  {
    id: "hall-structure",
    label: "ساختار سالن",
    description: "ساختار کلی سالن و مسیر راه‌اندازی",
    notice:
      "Wizard ساختار سالن در R2 تکمیل می‌شود. فعلاً برای ویرایش واقعی از بخش تانک‌ها و مدارهای آب استفاده کنید.",
  },
  {
    id: "tanks",
    label: "تانک‌ها",
    description: "تعریف و ویرایش تانک‌های سالن",
  },
  {
    id: "water-circuits",
    label: "مدارهای آب",
    description: "مدارها، اتصال تانک‌ها و پروفایل آب",
  },
  {
    id: "feeding-program",
    label: "برنامه غذا",
    description: "سیاست و زمان‌بندی تغذیه سالن",
  },
  {
    id: "workers-permissions",
    label: "دسترسی‌ها و نقش‌ها",
    description: "مجوزهای نقش‌ها و دسترسی اختصاصی کاربران",
  },
  {
    id: "hall-plan",
    label: "اعتبار و پلن سالن",
    description: "وضعیت پلن و مدت اعتبار سالن‌های موجود",
  },
  {
    id: "notifications-devices",
    label: "اعلان‌ها و دستگاه‌ها",
    description: "وضعیت اعلان و دستگاه‌های متصل",
  },
  {
    id: "qr-printing",
    label: "چاپ QR",
    description: "انتخاب و چاپ برچسب‌های QR تانک",
  },
  {
    id: "ai",
    label: "هوش مصنوعی",
    description: "تنظیم Provider و Gateway هوش مصنوعی",
  },
  {
    id: "system-version",
    label: "سیستم و نسخه",
    description: "نسخه برنامه، Runtime و وضعیت همگام‌سازی",
  },
  {
    id: "treatment-protocols",
    label: "پروتکل‌های درمان",
    description: "مدیریت پروتکل‌های درمانی موجود",
  },
];

export type SettingsHubAction = { type: "select"; surface: SettingsSurfaceId } | { type: "back" };

export function settingsHubNextSurface(
  current: SettingsSurfaceId | undefined,
  action: SettingsHubAction,
): SettingsSurfaceId | undefined {
  if (action.type === "back") return undefined;
  if (action.surface === current) return current;
  return action.surface;
}

interface SettingsHubProps {
  aiEnabled: boolean;
  renderPanel: (surface: SettingsSurfaceId) => ReactNode;
  initialSurface?: SettingsSurfaceId;
}

export function SettingsHub({ aiEnabled, renderPanel, initialSurface }: SettingsHubProps) {
  const [selectedSurface, setSelectedSurface] = useState<SettingsSurfaceId | undefined>(
    initialSurface,
  );
  const visibleEntries = SETTINGS_HUB_ENTRIES.filter((entry) => entry.id !== "ai" || aiEnabled);
  const selectedEntry = visibleEntries.find((entry) => entry.id === selectedSurface);

  if (selectedEntry) {
    return (
      <>
        <section className="section-card" aria-labelledby="settings-surface-title">
          <div className="section-title-row">
            <div>
              <p className="section-kicker">تنظیمات</p>
              <h2 id="settings-surface-title">{selectedEntry.label}</h2>
              <p className="section-note">{selectedEntry.description}</p>
            </div>
            <button
              type="button"
              className="text-button"
              onClick={() =>
                setSelectedSurface((current) => settingsHubNextSurface(current, { type: "back" }))
              }
            >
              بازگشت به تنظیمات
            </button>
          </div>

          {selectedEntry.notice ? <p className="settings-warning">{selectedEntry.notice}</p> : null}

          {selectedEntry.id === "hall-structure" ? (
            <div className="quick-grid">
              <button
                type="button"
                className="work-item pending"
                style={{ minHeight: "3rem", textAlign: "right" }}
                onClick={() =>
                  setSelectedSurface((current) =>
                    settingsHubNextSurface(current, { type: "select", surface: "tanks" }),
                  )
                }
              >
                <span className="work-main">
                  <strong>ویرایش تانک‌ها</strong>
                  <span>ویرایش دستی ساختار فعلی</span>
                </span>
              </button>
              <button
                type="button"
                className="work-item pending"
                style={{ minHeight: "3rem", textAlign: "right" }}
                onClick={() =>
                  setSelectedSurface((current) =>
                    settingsHubNextSurface(current, {
                      type: "select",
                      surface: "water-circuits",
                    }),
                  )
                }
              >
                <span className="work-main">
                  <strong>مدارهای آب</strong>
                  <span>ویرایش توپولوژی موجود</span>
                </span>
              </button>
            </div>
          ) : null}
        </section>

        {renderPanel(selectedEntry.id)}
      </>
    );
  }

  return (
    <section className="section-card" aria-labelledby="settings-hub-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">تنظیمات</p>
          <h2 id="settings-hub-title">مرکز تنظیمات</h2>
          <p className="section-note">هر بخش فقط پس از ورود شما باز و بارگذاری می‌شود.</p>
        </div>
      </div>

      <div className="quick-grid">
        {visibleEntries.map((entry) => (
          <button
            type="button"
            className="work-item pending"
            style={{ minHeight: "3rem", textAlign: "right", width: "100%" }}
            key={entry.id}
            onClick={() =>
              setSelectedSurface((current) =>
                settingsHubNextSurface(current, { type: "select", surface: entry.id }),
              )
            }
          >
            <span className="work-main">
              <strong>{entry.label}</strong>
              <span>{entry.description}</span>
            </span>
            <span className="work-status" aria-hidden="true">
              ←
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
